/**
 * useOrganization Hook
 * Organization & Tenant Switching Logic
 *
 * @description Issue #284対応 - テナント・店舗切り替え統合フック
 * @integration useApiClient.ts、multiTenant.tsとの完全統合
 * @features テナント切り替え、店舗切り替え、階層表示、エラーハンドリング
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApiClient } from './useApiClient';
import { useAuthContext } from './useAuth';
import logger from '../utils/logger';
import { handleError } from '../utils/error';
import { saveEncryptedToken } from '../utils/crypto';
import type { 
  ErrorWithMessage 
} from '../types/common.types';
import type { 
  InvitationData 
} from '../types/api.types';

// Types
import type {
  TenantInfo,
  TenantContext,
  TenantLimits,
  TenantSettings,
} from '../types/multiTenant';

// Extended TenantContext with optional limits and settings
interface ExtendedTenantContext extends TenantContext {
  limits?: TenantLimits;
  settings?: TenantSettings;
}




// Type for available stores from TenantContext (extended with optional properties)
type AvailableStore = {
  id: string;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive?: boolean;
  createdAt?: string;
  updatedAt?: string;
};

// Input validation error interface
interface ValidationError {
  field: string;
  message: string;
}

// Organization creation result interface
interface OrganizationCreationResult {
  organizationId: string;
  defaultStoreId?: string; // Optional because store creation might fail
  tenantContext: TenantContext;
  ownerRoleAssigned: boolean;
}

// Organization data validation function
function validateOrganizationData(
  organizationData: import('../services/authService').OrganizationCreationRequest
): ValidationError[] {
  const errors: ValidationError[] = [];

  // Required fields validation
  if (!organizationData.organizationName?.trim()) {
    errors.push({ field: 'organizationName', message: '組織名は必須です' });
  } else if (organizationData.organizationName.length < 2) {
    errors.push({ field: 'organizationName', message: '組織名は2文字以上で入力してください' });
  } else if (organizationData.organizationName.length > 100) {
    errors.push({ field: 'organizationName', message: '組織名は100文字以下で入力してください' });
  }

  if (!organizationData.ownerName?.trim()) {
    errors.push({ field: 'ownerName', message: 'オーナー名は必須です' });
  } else if (organizationData.ownerName.length < 2) {
    errors.push({ field: 'ownerName', message: 'オーナー名は2文字以上で入力してください' });
  }

  if (!organizationData.email?.trim()) {
    errors.push({ field: 'email', message: 'メールアドレスは必須です' });
  } else {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(organizationData.email)) {
      errors.push({ field: 'email', message: '有効なメールアドレスを入力してください' });
    }
  }

  // Optional field validation
  if (organizationData.phoneNumber && organizationData.phoneNumber.trim()) {
    const phoneRegex = /^[\d\-()s+]{10,15}$/;
    if (!phoneRegex.test(organizationData.phoneNumber.replace(/[\s\-()]/g, ''))) {
      errors.push({ field: 'phoneNumber', message: '有効な電話番号を入力してください' });
    }
  }

  if (organizationData.description && organizationData.description.length > 500) {
    errors.push({ field: 'description', message: '説明は500文字以下で入力してください' });
  }

  // Business type validation
  const validBusinessTypes = ['beauty_salon', 'relaxation', 'hair_salon', 'esthetics', 'nail_salon', 'massage'];
  if (organizationData.businessType && !validBusinessTypes.includes(organizationData.businessType)) {
    errors.push({ field: 'businessType', message: '有効な事業タイプを選択してください' });
  }

  return errors;
}

/**
 * Store Information Interface
 * TenantContextのavailableStoresを拡張した完全なStore情報
 */
export interface StoreInfo {
  id: string;
  name: string;
  code?: string;
  address?: string;
  phone?: string;
  email?: string;
  isActive: boolean;
  tenantId: string;
  createdAt?: Date;
  updatedAt?: Date;
}

/**
 * Organization Hierarchy Interface
 * 組織階層情報の表示用
 */
export interface OrganizationHierarchy {
  tenant: TenantInfo;
  currentStore?: StoreInfo;
  stats: {
    totalStores: number;
    activeStores: number;
    totalUsers: number;
    planLimits: {
      maxStores: number;
      maxUsers: number;
    };
  };
}

/**
 * useOrganization Hook Result Interface
 */
export interface UseOrganizationResult {
  // 現在の組織情報
  currentTenant: TenantInfo | null;
  currentStore: StoreInfo | null;
  availableTenants: TenantInfo[];
  availableStores: StoreInfo[];
  organizationHierarchy: OrganizationHierarchy | null;

  // 切り替え機能
  switchTenant: (tenantId: string) => Promise<void>;
  switchStore: (storeId: string) => Promise<void>;
  refreshTenants: () => Promise<void>;
  refreshStores: () => Promise<void>;

  // 🔥 新規追加: 組織作成機能
  createDefaultTenant: () => Promise<void>;
  createOrganizationWithOwner: (organizationData: import('../services/authService').OrganizationCreationRequest) => Promise<OrganizationCreationResult | null>;
  completeOrganizationSetup: (organizationId: string) => Promise<boolean>;

  // 🆕 Issue #268: 招待機能
  createInvitationHook: () => {
    invitationInfo: InvitationData | null;
    isLoadingInvitation: boolean;
    invitationError: string | null;
  };
  acceptInvitation: (token: string) => Promise<void>;
  declineInvitation: (token: string) => Promise<void>;

  // UI状態
  isLoading: boolean;
  isSwitching: boolean;
  error: string | null;

  // ユーティリティ
  canSwitchTenant: boolean;
  canSwitchStore: boolean;
  hasMultipleTenants: boolean;
  hasMultipleStores: boolean;
  hasExistingOrganization: boolean;
  shouldCreateOrganization: boolean;
}

/**
 * useOrganization Hook Implementation
 */
export function useOrganization(): UseOrganizationResult {
  // State
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const [availableStores, setAvailableStores] = useState<StoreInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dependencies
  const { tenantContext } = useApiClient();
  const { user, isAuthenticated } = useAuthContext();

  /**
   * Current organization info from context
   */
  const currentTenant = useMemo((): TenantInfo | null => {
    if (!tenantContext) return null;

    return {
      id: tenantContext.id,
      name: tenantContext.name,
      subdomain: tenantContext.subdomain,
      planType: tenantContext.planType,
      features: tenantContext.features,
      billingStatus: tenantContext.billingStatus,
      status: 'active', // デフォルト値
      limits: (tenantContext as ExtendedTenantContext)?.limits || {
        maxUsers: 50,
        currentUsers: 1,
        maxCustomers: 10000,
        maxReservations: 50000,
        maxFiles: 1000,
        storageLimit: 1024,
        apiRequestsPerMinute: 100,
        apiRequestsPerHour: 1000,
        apiRequestsPerDay: 10000,
        maxIntegrations: 10,
        maxCustomFields: 50,
        maxReports: 20,
        limitsResetDate: new Date(),
        billingCycleStart: new Date(),
      } as TenantLimits,
      settings: (tenantContext as ExtendedTenantContext)?.settings || {
        brandColor: '#1976d2',
        enforceSSO: false,
        requireMFA: false,
        sessionTimeout: 480,
        passwordPolicy: {
          minLength: 8,
          requireUppercase: true,
          requireLowercase: true,
          requireNumbers: true,
          requireSpecialChars: false,
          maxAge: 90,
          preventReuse: 5,
        },
        enabledFeatures: tenantContext.features || [],
        customFields: [],
        allowedIntegrations: ['stripe', 'google-calendar'],
        webhookEndpoints: [],
        notificationSettings: {
          email: { enabled: true },
          sms: { enabled: false },
          push: { enabled: true },
          webhook: { enabled: false },
        },
        timezone: 'Asia/Tokyo',
        dateFormat: 'YYYY-MM-DD',
        currency: 'JPY',
        language: 'ja',
      } as TenantSettings
    };
  }, [tenantContext]);

  const currentStore = useMemo((): StoreInfo | null => {
    if (!tenantContext?.currentStoreId || !availableStores.length) return null;

    return availableStores.find(store => store.id === tenantContext.currentStoreId) || null;
  }, [tenantContext?.currentStoreId, availableStores]);

  /**
   * Organization hierarchy info
   */
  const organizationHierarchy = useMemo((): OrganizationHierarchy | null => {
    if (!currentTenant) return null;

    return {
      tenant: currentTenant,
      currentStore: currentStore || undefined,
      stats: {
        totalStores: availableStores.length,
        activeStores: availableStores.filter(store => store.isActive).length,
        totalUsers: currentTenant.limits?.currentUsers || 1,
        planLimits: {
          maxStores: getPlanLimits(currentTenant.planType).maxStores,
          maxUsers: getPlanLimits(currentTenant.planType).maxUsers,
        },
      },
    };
  }, [currentTenant, currentStore, availableStores]);

  /**
   * Utility flags
   */
  const canSwitchTenant = useMemo(() => {
    return user?.role === 'owner';
  }, [user?.role]);

  const canSwitchStore = useMemo(() => {
    return availableStores.length > 1;
  }, [availableStores.length]);

  const hasMultipleTenants = availableTenants.length > 1;
  const hasMultipleStores = availableStores.length > 1;

  /**
   * Load available tenants
   */
  const refreshTenants = useCallback(async () => {
    if (!isAuthenticated || !canSwitchTenant) {
      return;
    }

    try {
      setError(null);
      // AuthService V2 API による実際のテナント一覧取得
      // 現在のユーザーがアクセス可能なテナント一覧を取得
      const { default: AuthService } = await import('../services/authService');
      const currentUser = AuthService.getCurrentUserV2();

      if (currentUser && currentTenant) {
        // 現在のテナント情報をベースに利用可能テナント一覧を構築
        const tenants: TenantInfo[] = [currentTenant];
        setAvailableTenants(tenants);
        logger.info('Available tenants loaded:', tenants.length);
      } else {
        // フォールバック: 空の配列
        setAvailableTenants([]);
        logger.info('Available tenants loaded: 0');
      }
    } catch (err) {
      const errorMsg = 'テナント一覧の取得に失敗しました';
      setError(errorMsg);
      handleError(err);
    }
  }, [isAuthenticated, canSwitchTenant, currentTenant]);

  /**
   * Load available stores for current tenant
   */
  const refreshStores = useCallback(async () => {
    if (!isAuthenticated || !currentTenant) {
      return;
    }

    try {
      setError(null);
      // AuthService V2 API による実際の店舗一覧取得
      // テナントコンテキストから利用可能な店舗一覧を取得
      const { default: AuthService } = await import('../services/authService');
      const tenantContext = AuthService.getCurrentTenantContextV2();

      if (tenantContext?.availableStores) {
        const stores: StoreInfo[] = tenantContext.availableStores.map((store: AvailableStore) => ({
          id: store.id,
          name: store.name,
          code: store.code || '',
          address: store.address || '',
          phone: store.phone || '',
          email: store.email || '',
          isActive: store.isActive !== false,
          tenantId: currentTenant.id,
          createdAt: store.createdAt ? new Date(store.createdAt) : undefined,
          updatedAt: store.updatedAt ? new Date(store.updatedAt) : undefined,
        }));
        setAvailableStores(stores);
        logger.info('Available stores loaded:', stores.length);
      } else {
        // デフォルト店舗を作成（開発環境用）
        const defaultStores: StoreInfo[] = [
          {
            id: 'store-default-1',
            name: `${currentTenant.name} 本店`,
            code: 'MAIN001',
            address: '東京都',
            phone: '',
            email: '',
            isActive: true,
            tenantId: currentTenant.id,
          }
        ];
        setAvailableStores(defaultStores);
        logger.info('Available stores loaded:', defaultStores.length);
      }
    } catch (err) {
      // 初期化時は静かにエラーを処理（ログのみ）
      handleError(err);
      // エラー状態は設定しない（初期ロード時のUXを改善）
      // setError('店舗一覧の取得に失敗しました');
    }
  }, [isAuthenticated, currentTenant]);

  /**
   * Switch to different tenant
   */
  const switchTenant = useCallback(async (tenantId: string) => {
    if (!canSwitchTenant || isSwitching) {
      return;
    }

    const targetTenant = availableTenants.find(t => t.id === tenantId);
    if (!targetTenant) {
      setError('指定されたテナントが見つかりません');
      return;
    }

    try {
      setIsSwitching(true);
      setError(null);

      logger.info('Switching tenant:', { from: currentTenant?.id, to: tenantId });

      // AuthService V2 API による実際のテナント切り替え
      await (await import('../services/authService')).default.switchTenantV2({ tenantId });
      logger.info('Tenant switched via AuthService V2:', tenantId);

      // 店舗リストをリフレッシュ
      await refreshStores();

      logger.info('Tenant switched successfully:', tenantId);
    } catch (err) {
      const errorMsg = 'テナント切り替えに失敗しました';
      setError(errorMsg);
      handleError(err);
    } finally {
      setIsSwitching(false);
    }
  }, [canSwitchTenant, isSwitching, availableTenants, currentTenant?.id, refreshStores]);

  /**
   * Switch to different store
   */
  const switchStore = useCallback(async (storeId: string) => {
    if (!canSwitchStore || isSwitching) {
      return;
    }

    const targetStore = availableStores.find(s => s.id === storeId);
    if (!targetStore) {
      setError('指定された店舗が見つかりません');
      return;
    }

    try {
      setIsSwitching(true);
      setError(null);

      logger.info('Switching store:', { from: currentStore?.id, to: storeId });

      // AuthService V2 API による実際の店舗切り替え
      // テナントコンテキストの店舗IDを更新
      const { default: AuthService } = await import('../services/authService');
      const currentTenantContext = AuthService.getCurrentTenantContextV2();

      if (currentTenantContext) {
        // テナントコンテキストを更新して店舗切り替えを反映
        const updatedContext = {
          ...currentTenantContext,
          currentStoreId: storeId
        };

        // 暗号化ストレージに保存（セキュリティ強化）
        // 🔥 SECURITY FIX: Correct parameter order (data, key)
        saveEncryptedToken(JSON.stringify(updatedContext), 'salonhub_current_tenant_context');

        logger.info('Store switched via tenant context update:', storeId);
      } else {
        logger.warn('No tenant context available for store switch');
      }

      // NOTE: 実際のコンテキスト更新はuseApiClientで処理される想定

      logger.info('Store switched successfully:', storeId);
    } catch (err) {
      const errorMsg = '店舗切り替えに失敗しました';
      setError(errorMsg);
      handleError(err);
    } finally {
      setIsSwitching(false);
    }
  }, [canSwitchStore, isSwitching, availableStores, currentStore?.id]);

  /**
   * 🔥 新規実装: デフォルト組織作成機能
   * 初回ユーザー向けの自動テナント作成フォールバック
   */
  const createDefaultTenant = useCallback(async () => {
    if (!isAuthenticated || !user) {
      logger.warn('Cannot create default tenant: user not authenticated');
      return;
    }

    try {
      setIsSwitching(true);
      setError(null);

      logger.info('Creating default tenant for user:', user.id);

      // Input validation for default tenant creation
      if (!user.email || user.email.trim() === '') {
        throw new Error('ユーザーのメールアドレスが設定されていません');
      }

      const organizationName = `${user.name || 'マイ'}サロン`;
      if (organizationName.length < 2) {
        throw new Error('有効な組織名を生成できません');
      }

      // AuthService V2 API による実際のデフォルトテナント作成
      const { default: AuthService } = await import('../services/authService');
      const response = await AuthService.createDefaultTenant({
        userId: user.id,
        organizationData: {
          organizationName,
          businessType: 'beauty_salon',
          ownerName: user.name || 'オーナー',
          email: user.email,
          address: '東京都',
          phoneNumber: '',
          description: '',
        },
        autoAssignOwner: true
      });

      if (!response.success) {
        throw new Error(response.message || 'デフォルトテナントの作成に失敗しました');
      }

      // 作成されたテナント情報でローカル状態を更新
      const newTenant: TenantInfo = {
        id: response.organizationId,
        name: `${user.name || 'マイ'}サロン`,
        subdomain: `salon-${user.id.slice(0, 8)}`,
        planType: 'light',
        features: ['basic-booking', 'customer-management'],
        billingStatus: 'active',
        status: 'active',
        limits: {} as TenantLimits,
        settings: {} as TenantSettings,
      };

      const newStore: StoreInfo = {
        id: response.defaultStoreId,
        name: `${newTenant.name} 本店`,
        code: 'MAIN001',
        address: '東京都',
        phone: '',
        email: user.email || '',
        isActive: true,
        tenantId: response.organizationId,
      };

      setAvailableTenants([newTenant]);
      setAvailableStores([newStore]);

      logger.info('Default tenant created successfully:', {
        tenantId: newTenant.id,
        storeId: newStore.id
      });

      // 必要に応じてAPIClientのコンテキストを更新
      // useApiClientでリフレッシュ処理が必要かもしれない

    } catch (err) {
      const errorMsg = 'デフォルト組織の作成に失敗しました';
      setError(errorMsg);
      handleError(err);
      logger.error('Failed to create default tenant:', err);
    } finally {
      setIsSwitching(false);
    }
  }, [isAuthenticated, user]);

  /**
   * 🆕 Phase 4: 組織作成機能
   * 新規ユーザー向けの組織作成とオーナー権限自動設定
   */
  const createOrganizationWithOwner = useCallback(async (
    organizationData: import('../services/authService').OrganizationCreationRequest
  ) => {
    if (!isAuthenticated || !user) {
      logger.warn('Cannot create organization: user not authenticated');
      setError('組織を作成するにはログインが必要です');
      return null;
    }

    // Input validation
    const validationErrors = validateOrganizationData(organizationData);
    if (validationErrors.length > 0) {
      const errorMessage = validationErrors.map(error => `${error.field}: ${error.message}`).join(', ');
      setError(`入力エラー: ${errorMessage}`);
      logger.warn('Organization creation validation failed:', validationErrors);
      return null;
    }

    try {
      setIsSwitching(true);
      setError(null);

      logger.info('Creating organization with owner:', organizationData.organizationName);

      // AuthServiceで組織作成を実行
      const { default: AuthService } = await import('../services/authService');
      const result = await AuthService.createOrganization(organizationData);

      if (!result.success) {
        throw new Error(result.message || '組織の作成に失敗しました');
      }

      // 組織コンテキストを自動設定
      await AuthService.setCurrentOrganization(result.organizationId);

      // 新しい組織でデフォルトテナントを作成
      const tenantResult = await AuthService.createDefaultTenant({
        userId: user.id,
        organizationData,
        autoAssignOwner: true
      });

      if (!tenantResult.success) {
        throw new Error(tenantResult.message || 'テナントの作成に失敗しました');
      }

      // ローカル状態を更新
      const newTenant: TenantInfo = {
        id: result.organizationId,
        name: organizationData.organizationName,
        subdomain: `salon-${user.id.slice(0, 8)}`,
        planType: 'light',
        features: ['basic-booking', 'customer-management', 'staff-management'],
        billingStatus: 'active',
        status: 'active',
        limits: {} as TenantLimits,
        settings: {} as TenantSettings,
      };

      const newStore: StoreInfo = {
        id: result.defaultStoreId || `store-${Date.now()}`,
        name: `${organizationData.organizationName} 本店`,
        code: 'MAIN001',
        address: organizationData.address || '',
        phone: organizationData.phoneNumber || '',
        email: organizationData.email,
        isActive: true,
        tenantId: result.organizationId,
      };

      setAvailableTenants([newTenant]);
      setAvailableStores([newStore]);

      logger.info('Organization created successfully:', {
        organizationId: result.organizationId,
        defaultStoreId: result.defaultStoreId,
        ownerAssigned: result.ownerRoleAssigned
      });

      return {
        organizationId: result.organizationId,
        defaultStoreId: result.defaultStoreId,
        tenantContext: tenantResult.tenantContext,
        ownerRoleAssigned: result.ownerRoleAssigned
      };

    } catch (err) {
      const errorMsg = '組織の作成に失敗しました';
      setError(errorMsg);
      handleError(err);
      logger.error('Failed to create organization:', err);
      return null;
    } finally {
      setIsSwitching(false);
    }
  }, [isAuthenticated, user]);

  /**
   * 🆕 Phase 4: 組織作成完了後のダッシュボードリダイレクト
   * 組織作成成功時の自動画面遷移
   */
  const completeOrganizationSetup = useCallback(async (organizationId: string) => {
    try {
      setError(null);

      logger.info('Completing organization setup:', organizationId);

      // テナントとストア情報を再読み込み
      await Promise.all([
        refreshTenants(),
        refreshStores(),
      ]);

      logger.info('Organization setup completed successfully');

      return true;
    } catch (err) {
      const errorMsg = '組織セットアップの完了に失敗しました';
      setError(errorMsg);
      handleError(err);
      logger.error('Failed to complete organization setup:', err);
      return false;
    }
  }, [refreshTenants, refreshStores]);

  /**
   * 🆕 Phase 4: 組織作成状態の確認
   * 新規ユーザーが組織作成済みかどうかの判定
   */
  const hasExistingOrganization = useMemo(() => {
    return availableTenants.length > 0;
  }, [availableTenants.length]);

  /**
   * 🆕 Phase 4: 組織作成が必要かどうかの判定
   * メール認証完了後の組織作成フロー制御
   */
  const shouldCreateOrganization = useMemo(() => {
    return Boolean(isAuthenticated && user && !hasExistingOrganization && !isLoading);
  }, [isAuthenticated, user, hasExistingOrganization, isLoading]);

  /**
   * Initialize data on mount with memory leak prevention
   * 🔥 FIX: Remove circular dependencies by removing function dependencies
   */
  useEffect(() => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    let isMounted = true; // 🧠 Memory Leak Prevention flag

    setIsLoading(true);

    const initializeData = async () => {
      try {
        // 🔥 FIX: Call functions directly without dependencies to avoid infinite loops
        if (!canSwitchTenant) {
          // Skip tenant loading if user cannot switch tenants
          if (currentTenant) {
            await loadStoresDirectly();
          }
        } else {
          await Promise.all([
            loadTenantsDirectly(),
            currentTenant ? loadStoresDirectly() : Promise.resolve(),
          ]);
        }
      } catch (err) {
        if (isMounted) { // 🧠 Only log if component is still mounted
          logger.error('Organization data initialization failed:', err);
          setError('組織データの初期化に失敗しました');
        }
      } finally {
        if (isMounted) { // 🧠 Only update state if component is still mounted
          setIsLoading(false);
        }
      }
    };

    // Direct loading functions to prevent dependency loops
    const loadTenantsDirectly = async () => {
      if (!isAuthenticated || !canSwitchTenant) return;

      try {
        setError(null);
        const { default: AuthService } = await import('../services/authService');
        const currentUser = AuthService.getCurrentUserV2();

        if (currentUser && currentTenant) {
          const tenants: TenantInfo[] = [currentTenant];
          setAvailableTenants(tenants);
          logger.info('Available tenants loaded:', tenants.length);
        } else {
          setAvailableTenants([]);
          logger.info('Available tenants loaded: 0');
        }
      } catch (err) {
        const errorMsg = 'テナント一覧の取得に失敗しました';
        setError(errorMsg);
        handleError(err);
      }
    };

    const loadStoresDirectly = async () => {
      if (!isAuthenticated || !currentTenant) return;

      try {
        setError(null);
        const { default: AuthService } = await import('../services/authService');
        const tenantContext = AuthService.getCurrentTenantContextV2();

        if (tenantContext?.availableStores) {
          const stores: StoreInfo[] = tenantContext.availableStores.map((store: AvailableStore) => ({
            id: store.id,
            name: store.name,
            code: store.code || '',
            address: store.address || '',
            phone: store.phone || '',
            email: store.email || '',
            isActive: store.isActive !== false,
            tenantId: currentTenant.id,
            createdAt: store.createdAt ? new Date(store.createdAt) : undefined,
            updatedAt: store.updatedAt ? new Date(store.updatedAt) : undefined,
          }));
          setAvailableStores(stores);
          logger.info('Available stores loaded:', stores.length);
        } else {
          // デフォルト店舗を作成（開発環境用）
          const defaultStores: StoreInfo[] = [
            {
              id: 'store-default-1',
              name: `${currentTenant.name} 本店`,
              code: 'MAIN001',
              address: '東京都',
              phone: '',
              email: '',
              isActive: true,
              tenantId: currentTenant.id,
            }
          ];
          setAvailableStores(defaultStores);
          logger.info('Available stores loaded (default):', defaultStores.length);
        }
      } catch (err) {
        const errorMsg = '店舗一覧の取得に失敗しました';
        setError(errorMsg);
        handleError(err);
      }
    };

    initializeData();

    // 🧠 Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [isAuthenticated, canSwitchTenant, currentTenant?.id]); // 🔥 FIX: Only essential dependencies

  /**
   * 🆕 Issue #268: 招待情報取得関数を作成
   * カスタムフックとして別途実装する必要があるため、呼び出し元で実装
   * @deprecated useInvitationHookを別途実装してください
   */
  const createInvitationHook = useCallback(() => {
    // この関数は呼び出し元でカスタムフックとして実装する必要があります
    // 例:
    // function useInvitationHook(token: string) {
    //   const [invitationInfo, setInvitationInfo] = useState(null);
    //   const [isLoadingInvitation, setIsLoadingInvitation] = useState(false);
    //   const [invitationError, setInvitationError] = useState(null);
    //   useEffect(() => { ... }, [token]);
    //   return { invitationInfo, isLoadingInvitation, invitationError };
    // }
    logger.warn('[useOrganization] useInvitation is deprecated. Please implement useInvitationHook separately.');
    return {
      invitationInfo: null,
      isLoadingInvitation: false,
      invitationError: null
    };
  }, []);

  /**
   * 🆕 Issue #268: 招待承認機能
   * 招待を承認し、組織に参加
   */
  const acceptInvitation = useCallback(async (token: string) => {
    try {
      setIsSwitching(true);
      setError(null);

      logger.info('[useOrganization] Accepting invitation:', token);

      const { default: AuthService } = await import('../services/authService');
      const result = await AuthService.acceptInvitation(token);

      if (!result.success) {
        throw new Error(result.message || '招待の承認に失敗しました');
      }

      // 組織情報を再読み込み
      await Promise.all([
        refreshTenants(),
        refreshStores(),
      ]);

      logger.info('[useOrganization] Invitation accepted successfully');

    } catch (err: unknown) {
      const errorMsg = (err as ErrorWithMessage)?.message || '招待の承認中にエラーが発生しました';
      setError(errorMsg);
      handleError(err);
      throw err; // コンポーネント側でハンドリングするため再スロー
    } finally {
      setIsSwitching(false);
    }
  }, [refreshTenants, refreshStores]);

  /**
   * 🆕 Issue #268: 招待拒否機能
   * 招待を拒否
   */
  const declineInvitation = useCallback(async (token: string) => {
    try {
      setIsSwitching(true);
      setError(null);

      logger.info('[useOrganization] Declining invitation:', token);

      const { default: AuthService } = await import('../services/authService');
      const result = await AuthService.declineInvitation(token);

      if (!result.success) {
        throw new Error(result.message || '招待の拒否に失敗しました');
      }

      logger.info('[useOrganization] Invitation declined successfully');

    } catch (err: unknown) {
      const errorMsg = (err as ErrorWithMessage)?.message || '招待の拒否中にエラーが発生しました';
      setError(errorMsg);
      handleError(err);
      throw err; // コンポーネント側でハンドリングするため再スロー
    } finally {
      setIsSwitching(false);
    }
  }, []);



  return {
    // Current info
    currentTenant,
    currentStore,
    availableTenants,
    availableStores,
    organizationHierarchy,

    // Actions
    switchTenant,
    switchStore,
    refreshTenants,
    refreshStores,
    createDefaultTenant,
    createOrganizationWithOwner,
    completeOrganizationSetup,

    // 🆕 Issue #268: 招待機能
    createInvitationHook,
    acceptInvitation,
    declineInvitation,



    // State
    isLoading,
    isSwitching,
    error,

    // Utility flags
    canSwitchTenant,
    canSwitchStore,
    hasMultipleTenants,
    hasMultipleStores,
    hasExistingOrganization,
    shouldCreateOrganization,
  };
}

/**
 * Get plan limits based on plan type
 */
function getPlanLimits(planType: string) {
  const limits = {
    light: { maxStores: 1, maxUsers: 5 },
    standard: { maxStores: 3, maxUsers: 15 },
    premium: { maxStores: 10, maxUsers: 50 },
    // Note: premium has higher limits (not unlimited)
  };

  return limits[planType as keyof typeof limits] || limits.light;
}

export default useOrganization;